import { generateStringHash53Bit } from './utils.js';

/**
 * A list of event types that should fire once per page session. An event with the same type and
 * identical properties (excluding 'event_uuid') within the same page session is a duplicate. For
 * example, a 'web_impression' event with the same 'page_session_id', 'unit_name',
 * 'position_in_unit', etc., but different 'event_uuid' is a duplicate. 'event_uuid' is not included
 * in the hash for these events. All other event types include 'event_uuid' in their hash.
 */
const PAGE_SESSION_EVENTS_HASHED_WITHOUT_UUID = ['web_impression', 'web_pageview'];

/**
 * A list of event types that can be ignored and not added to the history.
 * Duplicates of these events don't impact reporting. For example, 'web_time_spent' events are based
 * on min/max timestamps, and 'web_performance_metric' events are aggregated, so duplicates don't
 * affect reporting on these events.
 */
const EVENT_TYPES_IGNORED = ['web_performance_metric', 'web_time_spent'];

/**
 * getEventHash generates a unique 53-bit hash for an event, derived from the event_data properties.
 *  1. Sorts event_data properties alphabetically.
 *  2. Exclude properties from the hashing process that are not relevant to event uniqueness.
 *  3. Use JSON.stringify() with the filtered properties as a replacer to create a string
 *     representation of the event_data. `event_data` has no nested objects, so no need for a
 *     custom replacer function.
 *  4. Generate a 53-bit hash from this string.
 * @param {*} event_data
 * @returns {String} - A 53-bit hash of the event_data object.
 */
const getEventHash = event_data => {
  const excluded_properties = ['event_ts', 'mode',
  // Can change with browser window resizing, creating new sets of hashes.
  'viewport_size' // Updates with any change in browser dimensions, creating new hashes.
  ];
  if (PAGE_SESSION_EVENTS_HASHED_WITHOUT_UUID.includes(event_data.type)) {
    excluded_properties.push('event_uuid');
  }
  const properties_sorted = Object.keys(event_data).sort();

  // Use `Set` for faster lookups where supported (O(n) vs O(1) compared to arrays). To support as
  // many clients as possible, a fallback is provided for the 1% of users without `Set` support.
  let excluded_properties_set;
  if (typeof Set === 'function') {
    excluded_properties_set = new Set(excluded_properties);
  }

  // Filter out the keys in the excluded_properties list
  const properties_filtered = properties_sorted.filter(property => excluded_properties_set ? !excluded_properties_set.has(property) : !excluded_properties.includes(property));
  return generateStringHash53Bit(JSON.stringify(event_data, properties_filtered));
};

/**
 * createClientEventHistory creates a history of client events.
 * @param {*} options.length - The maximum number of events to store in the history. 1000 should
 * limit memory to about 32kb.
 * @returns {Object} - An object with methods to push and check for events in the history.
 */
const createClientEventHistory = function () {
  let {
    length = 1000
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const history = [];
  return {
    /**
     * Adds an event to the history.
     * If the event type is in EVENT_TYPES_IGNORED, the event is not added.
     * If adding the event would exceed the maximum length of the history, the oldest event is removed.
     */
    push(event_data) {
      if (EVENT_TYPES_IGNORED.includes(event_data.type)) {
        return;
      }
      const eventHash = getEventHash(event_data);
      if (history.unshift(eventHash) > length) {
        history.length = length;
      }
    },
    includes(event_data) {
      const eventHash = getEventHash(event_data);
      return history.includes(eventHash);
    },
    // Used for debugging
    get history() {
      return history;
    },
    // Used for testing
    get length() {
      return history.length;
    }
  };
};

export { createClientEventHistory };
